/**
 * Refined Design — dragging a sideways row.
 *
 * Every row on the site that runs off the right of the screen scrolls natively: a
 * finger, a trackpad, a keyboard and the arrow buttons all work before this file
 * loads. A mouse is the one pointer left out — it has no swipe, only a scrollbar the
 * rows deliberately hide — so this adds the drag, and nothing else.
 *
 * Only a mouse or a pen ever gets here. Touch keeps the browser's own scrolling,
 * which carries momentum and rubber-banding no script should try to imitate.
 *
 * A drag is not a click: the grab only starts once the pointer has travelled past a
 * few pixels, so a press that lands on a swatch or a buy button still reaches it,
 * and the click that ends a real drag is swallowed on its way back up so letting go
 * over a card does not open it.
 *
 *   const detach = RdDragScroll.attach(el, { onStart, onEnd })
 */
(function () {
  /** How far the pointer travels before a press becomes a drag. */
  const THRESHOLD = 5;

  function attach(el, options) {
    if (!el || el.dataset.rdDragScroll) return () => {};
    el.dataset.rdDragScroll = '1';
    const opts = options || {};

    let pressX = 0;
    let lastX = 0;
    let pointer = null;
    let dragging = false;

    const onPointerDown = (event) => {
      if (event.pointerType === 'touch' || event.button !== 0) return;
      pointer = event.pointerId;
      pressX = event.clientX;
      lastX = event.clientX;
      dragging = false;
    };

    const onPointerMove = (event) => {
      if (event.pointerId !== pointer) return;
      if (!dragging) {
        if (Math.abs(event.clientX - pressX) < THRESHOLD) return;
        dragging = true;
        el.classList.add('is-dragging');
        // The row keeps receiving the move events even when the pointer runs off it,
        // so a fast drag does not stall the moment it leaves the cards.
        if (el.setPointerCapture) {
          try { el.setPointerCapture(pointer); } catch (e) { /* capture is a nicety */ }
        }
        if (opts.onStart) opts.onStart();
      }
      // Each move carries only what has changed since the last one, rather than the
      // distance from where the press landed. An endless ribbon jumps its scroll
      // position back a lap as the drag passes the seam, and a running total measured
      // from the press would undo that jump on the very next move.
      //
      // Smooth scrolling from the arrow buttons would fight a drag for the same
      // property; the drag is the more recent instruction, so it wins outright.
      el.style.scrollBehavior = 'auto';
      el.scrollLeft -= event.clientX - lastX;
      lastX = event.clientX;
      event.preventDefault();
    };

    const onPointerUp = (event) => {
      if (event.pointerId !== pointer) return;
      pointer = null;
      if (!dragging) return;
      dragging = false;
      el.classList.remove('is-dragging');
      el.style.scrollBehavior = '';
      // One click follows the release, aimed at whatever the pointer happens to be
      // over. It was a drag, not a choice.
      el.addEventListener('click', swallow, { capture: true, once: true });
      window.setTimeout(() => el.removeEventListener('click', swallow, { capture: true }), 0);
      if (opts.onEnd) opts.onEnd();
    };

    const swallow = (event) => {
      event.preventDefault();
      event.stopPropagation();
    };

    // Photos and links carry the browser's own drag-and-drop, which would otherwise
    // start the moment the pointer moves and leave a ghost image trailing the cursor.
    const onDragStart = (event) => {
      if (pointer !== null) event.preventDefault();
    };

    el.addEventListener('pointerdown', onPointerDown);
    el.addEventListener('pointermove', onPointerMove);
    el.addEventListener('pointerup', onPointerUp);
    el.addEventListener('pointercancel', onPointerUp);
    el.addEventListener('dragstart', onDragStart);

    return () => {
      el.removeEventListener('pointerdown', onPointerDown);
      el.removeEventListener('pointermove', onPointerMove);
      el.removeEventListener('pointerup', onPointerUp);
      el.removeEventListener('pointercancel', onPointerUp);
      el.removeEventListener('dragstart', onDragStart);
      el.dataset.rdDragScroll = '';
    };
  }

  window.RdDragScroll = { attach };
})();
